import {
  AlertColor,
  Box,
  Button,
  CircularProgress,
  Divider,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import dayjs from 'dayjs';
import DrawerMenu from 'components/DrawerMenu';
import Notification from 'components/Notification';
import {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import strings from '../../../../../common/Translation/Translate';
import axiosInstance from '../../../../../utils/axios';
import {URLS} from '../../../../../utils/constants/urls';
import Currency from '../../../../../utils/hooks/getCurrency';
import {getLineItemDrawerWidth} from '../../../../../utils/validators/HelperFunctions';
import {getReceivableTypeLabel} from '../AddReceivable/receivableUtils';

type AllocationItem = {
  componentType: string;
  componentId: number;
  itemName: string;
  venue?: string;
  totalAmount: number;
  amountPaid: number;
  amountDue: number;
};

type SettleReceivableDrawerProps = {
  open: boolean;
  onClose: () => void;
  bookingId: number;
  settlementEntry: any;
  paymentMethods: {value: string; label: string}[];
  onSuccess: () => void;
};

const allocationKey = (item: AllocationItem) => `${item.componentType}:${item.componentId}`;

export const SettleReceivableDrawer = ({
  open,
  onClose,
  bookingId,
  settlementEntry,
  paymentMethods,
  onSuccess,
}: SettleReceivableDrawerProps) => {
  const theme = useTheme();
  const screenSizeDownSm = useMediaQuery(theme.breakpoints.down('sm'));
  const screenSizeDownMd = useMediaQuery(theme.breakpoints.down('md'));
  const screenSizeUpLg = useMediaQuery(theme.breakpoints.up('lg'));

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  /** Sync guard so a second Save click in the same tick cannot start another POST. */
  const savingRef = useRef(false);
  const [allocationItems, setAllocationItems] = useState<AllocationItem[]>([]);
  const [allocations, setAllocations] = useState<Record<string, string>>({});
  const [amountReceived, setAmountReceived] = useState('');
  const [paymentMethod, setPaymentMethod] = useState('');
  const [paymentDate, setPaymentDate] = useState(dayjs().format('YYYY-MM-DD'));
  const [paymentReference, setPaymentReference] = useState('');
  const [formError, setFormError] = useState('');
  const [message, setMessage] = useState<{display: boolean; severity: AlertColor | null; message: string}>({
    display: false,
    severity: null,
    message: '',
  });

  const pendingAmount = Number(settlementEntry?.pendingAmount) || 0;

  const loadContext = useCallback(async () => {
    if (!settlementEntry?.id) return;
    setLoading(true);
    setFormError('');
    try {
      const {data} = await axiosInstance.get(
        URLS.getSettlementAllocationContext(bookingId, settlementEntry.id),
      );
      const items: AllocationItem[] = data?.allocationItems || [];
      setAllocationItems(items);
      setAmountReceived(String(pendingAmount));
      setAllocations({});
      setPaymentMethod(paymentMethods[0]?.value || '');
      setPaymentDate(dayjs().format('YYYY-MM-DD'));
      setPaymentReference('');
    } catch (error: any) {
      setFormError(
        error.response?.data?.error_description ||
          error.response?.data?.message ||
          strings.something_went_wrong,
      );
    } finally {
      setLoading(false);
    }
  }, [bookingId, settlementEntry?.id, paymentMethods, pendingAmount]);

  useEffect(() => {
    if (open) {
      loadContext();
    }
  }, [open, loadContext]);

  const totalAllocated = useMemo(
    () =>
      Object.values(allocations).reduce((sum, value) => {
        const num = parseFloat(value);
        return sum + (Number.isFinite(num) ? num : 0);
      }, 0),
    [allocations],
  );

  const amountReceivedNum = parseFloat(amountReceived) || 0;
  const balance = amountReceivedNum - totalAllocated;

  const handleAllocationChange = (item: AllocationItem, value: string) => {
    const key = allocationKey(item);
    setAllocations((prev) => ({...prev, [key]: value}));
  };

  const validateForm = () => {
    if (!amountReceivedNum || amountReceivedNum <= 0) {
      return strings.amountGreaterThanZero;
    }
    if (amountReceivedNum > pendingAmount) {
      return strings.receivable_settle_exceedsPending;
    }
    if (!paymentMethod) {
      return strings.receivable_settle_paymentMode;
    }
    if (!paymentDate) {
      return strings.receivable_settle_paymentDate;
    }
    if (Math.abs(balance) > 0.009) {
      return strings.receivable_settle_allocationMismatch;
    }

    for (const item of allocationItems) {
      const key = allocationKey(item);
      const allocated = parseFloat(allocations[key] || '0') || 0;
      if (allocated > item.amountDue + 0.009) {
        return strings.receivable_settle_exceedsDue;
      }
    }

    return '';
  };

  const handleSave = async () => {
    if (savingRef.current || saving) return;
    const validationError = validateForm();
    if (validationError) {
      setFormError(validationError);
      return;
    }

    const payload = {
      amountReceived: amountReceivedNum,
      paymentMethod,
      paymentDate,
      paymentReference,
      allocations: allocationItems
        .map((item) => ({
          componentType: item.componentType,
          componentId: item.componentId,
          amount: parseFloat(allocations[allocationKey(item)] || '0') || 0,
        }))
        .filter((row) => row.amount > 0),
    };

    savingRef.current = true;
    setSaving(true);
    setFormError('');
    try {
      await axiosInstance.post(URLS.settleSettlementEntry(bookingId, settlementEntry.id), payload);
      setMessage({display: true, severity: 'success', message: strings.common_save_message});
      onSuccess();
      onClose();
    } catch (error: any) {
      setFormError(
        error.response?.data?.error_description ||
          error.response?.data?.message ||
          strings.something_went_wrong,
      );
    } finally {
      savingRef.current = false;
      setSaving(false);
    }
  };

  const closeNotification = (value: boolean) => {
    setMessage((prev) => ({...prev, display: value}));
  };

  const settlementLabel = settlementEntry
    ? `${getReceivableTypeLabel(settlementEntry.type)} - ${settlementEntry.companyName || '—'}`
    : '';

  return (
    <>
      {message.display && (
        <Notification
          isOpen={message.display}
          message={message.message}
          severity={message.severity as AlertColor}
          closeNotification={closeNotification}
          autoHideDuration={2000}
        />
      )}
      <DrawerMenu
        open={open}
        handleClose={onClose}
        closeButton
        flexColumn
        width={getLineItemDrawerWidth(screenSizeDownSm, screenSizeDownMd, screenSizeUpLg)}
        title={strings.receivable_settle_title}>
        <Box sx={{display: 'flex', flexDirection: 'column', gap: 2, flex: 1, minHeight: 0}}>
          {loading ? (
            <Box sx={{display: 'flex', justifyContent: 'center', py: 6}}>
              <CircularProgress />
            </Box>
          ) : (
            <>
              <Box>
                <Typography variant="body2" color="text.secondary">
                  {strings.receivable_settle_settlementLabel}
                </Typography>
                <Typography variant="subtitle1" fontWeight={700}>
                  {settlementLabel}
                </Typography>
                <Typography variant="body2" sx={{mt: 1}}>
                  {strings.receivable_settle_pendingAmount}:{' '}
                  <Box component="span" sx={{fontWeight: 700, color: '#D93025'}}>
                    {settlementEntry?.formattedPendingAmount || Currency(pendingAmount)}
                  </Box>
                </Typography>
              </Box>

              <Divider />

              <Typography variant="subtitle2" fontWeight={700}>
                {strings.receivable_settle_paymentDetails}
              </Typography>
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: {xs: '1fr', sm: '1fr 1fr'},
                  gap: 2,
                }}>
                <TextField
                  required
                  size="small"
                  type="number"
                  label={strings.receivable_settle_amountReceived}
                  value={amountReceived}
                  onChange={(e) => setAmountReceived(e.target.value)}
                />
                <TextField
                  required
                  select
                  size="small"
                  label={strings.receivable_settle_paymentMode}
                  value={paymentMethod}
                  onChange={(e) => setPaymentMethod(e.target.value)}>
                  {paymentMethods.map((method) => (
                    <MenuItem key={method.value} value={method.value}>
                      {method.label}
                    </MenuItem>
                  ))}
                </TextField>
                <TextField
                  required
                  size="small"
                  type="date"
                  label={strings.receivable_settle_paymentDate}
                  value={paymentDate}
                  onChange={(e) => setPaymentDate(e.target.value)}
                  InputLabelProps={{shrink: true}}
                />
                <TextField
                  size="small"
                  label={strings.receivable_settle_referenceNo}
                  value={paymentReference}
                  onChange={(e) => setPaymentReference(e.target.value)}
                />
              </Box>

              <Divider />

              <Typography variant="subtitle2" fontWeight={700}>
                {strings.receivable_settle_allocateAgainst}
              </Typography>

              <TableContainer sx={{border: '1px solid', borderColor: 'divider', borderRadius: 2}}>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{bgcolor: '#fafafa'}}>
                      <TableCell>{strings.receivable_settle_column_item}</TableCell>
                      <TableCell align="right">{strings.receivable_settle_column_total}</TableCell>
                      <TableCell align="right">{strings.receivable_settle_column_paid}</TableCell>
                      <TableCell align="right">{strings.receivable_settle_column_due}</TableCell>
                      <TableCell align="right" sx={{minWidth: 120}}>
                        {strings.receivable_settle_column_allocate}
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {allocationItems.map((item) => {
                      const key = allocationKey(item);
                      return (
                        <TableRow key={key}>
                          <TableCell>
                            <Typography variant="body2" fontWeight={600}>
                              {item.itemName}
                            </Typography>
                            {item.venue && (
                              <Typography variant="caption" color="text.secondary">
                                {item.venue}
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell align="right">{Currency(item.totalAmount)}</TableCell>
                          <TableCell align="right" sx={{color: '#188038'}}>
                            {Currency(item.amountPaid)}
                          </TableCell>
                          <TableCell align="right" sx={{color: '#D93025', fontWeight: 600}}>
                            {Currency(item.amountDue)}
                          </TableCell>
                          <TableCell align="right">
                            <TextField
                              size="small"
                              type="number"
                              value={allocations[key] ?? ''}
                              onChange={(e) => handleAllocationChange(item, e.target.value)}
                              inputProps={{min: 0, max: item.amountDue, style: {textAlign: 'right'}}}
                              sx={{width: 110}}
                            />
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>

              <Box sx={{display: 'flex', justifyContent: 'flex-end', gap: 3}}>
                <Typography variant="body2">
                  {strings.receivable_settle_totalAllocated}:{' '}
                  <Box component="span" fontWeight={700}>
                    {Currency(totalAllocated)}
                  </Box>
                </Typography>
                <Typography variant="body2">
                  {strings.receivable_settle_balance}:{' '}
                  <Box
                    component="span"
                    fontWeight={700}
                    sx={{color: Math.abs(balance) < 0.01 ? '#188038' : '#D93025'}}>
                    {Currency(balance)}
                  </Box>
                </Typography>
              </Box>

              {formError && (
                <Typography variant="body2" color="error">
                  {formError}
                </Typography>
              )}

              <Box sx={{display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 1}}>
                <Button variant="outlined" onClick={onClose} disabled={saving}>
                  {strings.BookingDetailButonTextCancel}
                </Button>
                <Button variant="contained" onClick={handleSave} disabled={saving}>
                  {strings.receivable_settle_save}
                </Button>
              </Box>
            </>
          )}
        </Box>
      </DrawerMenu>
    </>
  );
};
